import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  BarChart3,
  LayoutDashboard,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import AnimateOnScroll, {
  StaggerContainer,
  StaggerItem,
} from "@/components/AnimateOnScroll";
import { useTranslation } from "react-i18next";
import { products } from "@/lib/data";

const iconMap: Record<string, any> = { BarChart3, LayoutDashboard, ShieldCheck, Workflow };

export default function ProdukPage() {
  const { t } = useTranslation();
  const translatedProducts = (t('home.products', { returnObjects: true }) || []) as any[];

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-30" />
        <div className="section-container section-padding relative z-10">
          <AnimateOnScroll className="text-center max-w-3xl mx-auto">
            <span className="text-neon-400 font-mono text-sm tracking-widest uppercase">
              {t('home.produk.title_kategori')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-6 gradient-text-white">
              {t('home.produk.title_main')} <span className="gradient-text">{t('home.produk.title_highlight')}</span>
            </h1>
            <p className="text-white/50 text-lg leading-relaxed">
              {t('home.produk.desc')}
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Products */}
      <section className="pb-24">
        <div className="section-container section-padding">
          <StaggerContainer className="space-y-8" staggerDelay={0.15}>
            {translatedProducts.map((product, index) => {
              const originalProduct = products[index] || products[0];
              const Icon = iconMap[originalProduct.icon];
              return (
                <StaggerItem key={originalProduct.slug}>
                  <div id={originalProduct.slug} className="scroll-mt-24">
                    <div className="glass-card-hover group p-8 md:p-10">
                      <div className="grid md:grid-cols-[auto_1fr] gap-8">
                        {originalProduct.thumbnail ? (
                          <div className="w-full md:w-72 h-48 md:h-auto rounded-2xl overflow-hidden flex-shrink-0 border border-white/10 relative">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"></div>
                            <img src={originalProduct.thumbnail} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-2xl bg-neon-500/10 flex items-center justify-center flex-shrink-0">
                            {Icon && <Icon className="w-10 h-10 text-neon-400" />}
                          </div>
                        )}
                        <div className="flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-2xl font-bold text-white">{product.name}</h2>
                            <span className="px-3 py-1 text-xs bg-neon-500/10 text-neon-400 rounded-full font-mono">
                              {product.category}
                            </span>
                          </div>
                          <p className="text-neon-400/70 font-medium mb-4">{product.tagline}</p>
                          <p className="text-white/50 leading-relaxed mb-6">{product.description}</p>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                            {product.features.map((feature: string) => (
                              <div key={feature} className="flex items-center gap-2 text-sm text-white/50">
                                <CheckCircle2 className="w-4 h-4 text-neon-500/50 flex-shrink-0" />
                                {feature}
                              </div>
                            ))}
                          </div>
                          {originalProduct.url && (
                            <div className="mt-auto pt-4">
                              <a href={originalProduct.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-neon-400 hover:text-neon-300 font-medium transition-colors group/link">
                                {t('home.produk.kunjungi_website')}
                                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32 pt-10 relative z-10">
        <div className="section-container section-padding text-center">
          <AnimateOnScroll>
            <h2 className="text-3xl md:text-4xl font-bold gradient-text-white mb-4">
              {t('home.produk.cta_title')}
            </h2>
            <p className="text-white/50 max-w-xl mx-auto mb-8">
              {t('home.produk.cta_desc')}
            </p>
            <Link to="/konsultasi" className="btn-primary">
              {t('home.produk.request_demo')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimateOnScroll>
        </div>
      </section>
    </>
  );
}
